import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { MapPin, MessageCircle, Mail, Instagram, CalendarCheck } from 'lucide-react';
import Header from '@/components/sections/Header';
import Footer from '@/components/sections/Footer';
import OgMeta from '@/components/OgMeta';

const WHATSAPP_URL =
  'https://api.whatsapp.com/send?phone=5541991360830&text=' +
  encodeURIComponent('Olá! Venho do site do Dr. Luis Brunello e gostaria de agendar uma consulta.');

const ContatoPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="min-h-screen bg-dark-base">
      <Helmet>
        <title>Contato | Dr. Luis Brunello — Cirurgião Plástico em Curitiba</title>
        <meta
          name="description"
          content="Fale com a equipe do Dr. Luis Brunello: WhatsApp, e-mail e endereço do consultório em Curitiba. Atendimento mediante agendamento prévio."
        />
        <link rel="canonical" href="https://luisbrunello.com.br/contato" />
      </Helmet>
      <OgMeta
        title="Contato | Dr. Luis Brunello — Cirurgião Plástico em Curitiba"
        description="Fale com a equipe do Dr. Luis Brunello: WhatsApp, e-mail e endereço do consultório em Curitiba. Atendimento mediante agendamento prévio."
        path="/contato"
      />

      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} scrollToSection={() => {}} onOpenBooking={() => {}} />

      <main className="pt-28 pb-20">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl">

          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-xs text-light-muted mb-10">
            <Link to="/" className="hover:text-gold transition-colors">Home</Link>
            <span aria-hidden="true">/</span>
            <span className="text-light-secondary" aria-current="page">Contato</span>
          </nav>

          <header className="mb-12">
            <p className="subtitle-wide text-gold mb-3 block">Fale conosco</p>
            <h1 className="text-3xl md:text-4xl text-light-primary tracking-tight leading-tight mb-4">
              Contato
            </h1>
            <p className="text-light-muted text-sm leading-relaxed">
              O consultório do Dr. Luis Brunello (CRM-PR 40.350 · RQE 38.853) atende em Curitiba, no Ecoville,
              dentro da Cirurgicare. O primeiro contato é feito pela equipe de atendimento, que esclarece dúvidas
              iniciais, explica como funciona a consulta e organiza a agenda. Todo atendimento é presencial e
              acontece mediante agendamento prévio — não há atendimento por ordem de chegada.
            </p>
          </header>

          <section aria-labelledby="canais-title" className="mb-12">
            <h2 id="canais-title" className="text-xl text-light-primary font-medium mb-6">Canais de atendimento</h2>

            <ul className="space-y-6 text-sm">
              <li className="flex items-start gap-3">
                <MessageCircle className="h-5 w-5 text-gold mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-light-secondary font-medium mb-0.5">WhatsApp — agendamento</p>
                  <p>
                    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                      WhatsApp: +55 (41) 99136-0830
                    </a>
                  </p>
                  <p className="text-light-muted mt-1 leading-relaxed">
                    Canal principal para marcar consulta, remarcar ou tirar dúvidas administrativas.
                    A equipe responde em horário comercial.
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-gold mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-light-secondary font-medium mb-0.5">E-mail</p>
                  <p>
                    <a href="mailto:contato@luisbrunello.com.br" className="text-gold hover:underline">
                      contato@luisbrunello.com.br
                    </a>
                  </p>
                  <p className="text-light-muted mt-1 leading-relaxed">
                    Para assuntos administrativos, imprensa, parcerias e solicitações relacionadas à
                    proteção de dados (LGPD).
                  </p>
                </div>
              </li>

              <li className="flex items-start gap-3">
                <Instagram className="h-5 w-5 text-gold mt-0.5 shrink-0" aria-hidden="true" />
                <div>
                  <p className="text-light-secondary font-medium mb-0.5">Instagram</p>
                  <p>
                    <a
                      href="https://www.instagram.com/luisbrunello/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold hover:underline"
                    >
                      @luisbrunello
                    </a>
                  </p>
                  <p className="text-light-muted mt-1 leading-relaxed">
                    Conteúdo sobre procedimentos, bastidores e orientações de pós-operatório.
                  </p>
                </div>
              </li>
            </ul>
          </section>

          <section aria-labelledby="endereco-title" className="mb-12">
            <h2 id="endereco-title" className="text-xl text-light-primary font-medium mb-6">Endereço do consultório</h2>
            <div className="flex items-start gap-3 text-sm">
              <MapPin className="h-5 w-5 text-gold mt-0.5 shrink-0" aria-hidden="true" />
              <div>
                <address className="not-italic text-light-secondary leading-relaxed">
                  <p>Cirurgicare</p>
                  <p>R. Prof. Pedro Viriato Parigot de Souza, 600 — Loja 3001-B</p>
                  <p>Mossunguê (Ecoville) · Curitiba — PR</p>
                  <p>CEP 81200-100</p>
                </address>
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Cirurgicare+Curitiba"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-3 text-gold hover:underline"
                >
                  Abrir no Google Maps
                </a>
              </div>
            </div>
          </section>

          <section aria-labelledby="consulta-title" className="mb-12">
            <h2 id="consulta-title" className="text-xl text-light-primary font-medium mb-6">Como funciona a primeira consulta</h2>
            <div className="flex items-start gap-3 text-sm">
              <CalendarCheck className="h-5 w-5 text-gold mt-0.5 shrink-0" aria-hidden="true" />
              <div className="text-light-muted space-y-3 leading-relaxed">
                <p>
                  A consulta inicial é presencial. Nela o Dr. Luis Brunello avalia o histórico de saúde,
                  examina a área de interesse, discute o que é e o que não é possível no seu caso e
                  apresenta as alternativas cirúrgicas — incluindo riscos e tempo de recuperação de cada uma.
                </p>
                <p>
                  Nenhuma conduta é definida por telefone, WhatsApp ou e-mail, e orçamentos não são passados
                  à distância: ambos dependem da avaliação presencial. Se preferir entender a sequência
                  completa antes de agendar, veja a{' '}
                  <Link to="/jornada" className="text-gold hover:underline">jornada do paciente</Link>.
                </p>
              </div>
            </div>
          </section>

          <div className="pt-8 border-t border-white/5">
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-pill btn-gold font-semibold text-sm inline-block"
            >
              Agendar pelo WhatsApp
            </a>
          </div>

        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContatoPage;
